// How a setup card's rows read (v3 `orpheusSetupCardPresentation.ts`, the row half): each row's
// label from the product form, its value formatted, and who filled it. The card's title, cost,
// confirm button and saved summary live in `orpheusSetupCardPresentation.ts`.
import type { SetupCard, SetupCardRow } from '../models/orpheusSetup.types'

export type Translate = (key: string, params?: Record<string, unknown>) => string

export interface SetupLabels {
  t: Translate
  /** The product's own name for a metric field, as the Alerts list and the reports show it. */
  metric: (field: string) => string
}

export const ALERT_WRITE = 'alerts.config_write'
export const KPI_WRITE = 'scorecards.write'
export const KPI_TARGET_UPDATE = 'scorecards.target_update'

const K = 'orpheus.setup'

const ROW_LABEL_KEYS: Record<string, Record<string, string>> = {
  [ALERT_WRITE]: {
    alert_name: 'alertName',
    level: 'level',
    group_by: 'groupBy',
    filter: 'filters',
    date_range: 'dateRange',
    metrics: 'metrics',
    trigger_alert_when: 'triggerAlertWhen',
    notification: 'notification',
    frequency: 'frequency',
    state: 'status',
  },
  [KPI_WRITE]: {
    kpi_name: 'kpiName',
    data_level: 'dataLevel',
    split_by: 'splitBy',
    period: 'period',
    file: 'file',
    targets: 'targets',
    boards: 'boards',
  },
}

const SUBJECT_KEYS: Record<string, string> = {
  [ALERT_WRITE]: 'alert',
  [KPI_WRITE]: 'kpi',
  [KPI_TARGET_UPDATE]: 'kpiTargets',
  'creative_ai.label_schema_write': 'labelSet',
  'creative_ai.labelling_run_start': 'labellingRun',
  'causal.run_start': 'impactRun',
}

const CONFIRM_KEYS: Record<string, string> = {
  [ALERT_WRITE]: 'createAlert',
  [KPI_WRITE]: 'createKpi',
  [KPI_TARGET_UPDATE]: 'updateTargets',
  'creative_ai.label_schema_write': 'saveLabels',
  'creative_ai.labelling_run_start': 'startLabelling',
  'causal.run_start': 'startAnalysis',
}

/**
 * Which setup-memory fields fill each alert row, so a row can say who filled it. The memory names
 * its fields after the first message's reading (`setup_memory.py`), not the write's inputs, so the
 * two vocabularies meet here. Rows of other writes fall back to their own input names.
 */
export const ALERT_ROW_SLOTS: Record<string, string[]> = {
  alert_name: ['name'],
  level: ['level'],
  group_by: ['dimensions'],
  filter: ['filters'],
  date_range: ['compare_window', 'comparison_mode'],
  metrics: ['metrics', 'metric'],
  trigger_alert_when: [
    'threshold',
    'direction',
    'thresholds',
    'threshold_type',
    'condition_operator',
    'deviation_percentage',
  ],
  notification: ['channel_type', 'allowed_emails'],
  frequency: ['frequency'],
  state: ['state'],
}

// ORP-27442: asked before a number is known, so it can stand alone in the rule's row.
const THRESHOLD_TYPE_KEYS: Record<string, string> = {
  value: 'fixedNumber',
  percentile: 'againstHistory',
}

const CHANNEL_KEYS: Record<string, string> = {
  mail: 'email',
  email: 'email',
  whatsapp: 'whatsapp',
  slack: 'slack',
  teams: 'teams',
}

// The alert wizard's own names for the comparison windows.
const COMPARE_WINDOW_KEYS: Record<string, string> = {
  previous_day: 'previousDay',
  same_day_last_week: 'sameDayLastWeek',
  same_day_last_month: 'sameDayLastMonth',
  previous_7_days: 'previous7Days',
  same_week_last_month: 'sameWeekLastMonth',
}

export type RowSource = 'user' | 'guess' | 'missing' | null

export interface RowValue {
  text: string
  chips: string[]
  /** True when the value is a default or empty and reads in the muted tone. */
  plain: boolean
}

export interface CardRowView {
  key: string
  label: string
  value: RowValue
  source: RowSource
}

export function humanize(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key
}

export function isBlank(value: unknown): boolean {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  )
}

export function scalar(value: unknown, t?: Translate): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') {
    if (!t) return value ? 'Yes' : 'No'
    return t(value ? `${K}.value.yes` : `${K}.value.no`)
  }
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, inner]) => !isBlank(inner) && typeof inner !== 'object')
      .map(([key, inner]) => `${humanize(key)}: ${String(inner)}`)
      .join(', ')
  }
  return String(value)
}

export function list(value: unknown): unknown[] {
  if (isBlank(value)) return []
  return Array.isArray(value) ? value : [value]
}

// Before a draft the model may hand a list over as one sentence: "mail, slack".
function items(value: unknown): unknown[] {
  return list(value).flatMap((item) =>
    typeof item === 'string'
      ? item
          .split(',')
          .map((part) => part.trim())
          .filter(Boolean)
      : [item],
  )
}

function plainValue(text: string): RowValue {
  return { text, chips: [], plain: true }
}

export function textValue(text: string): RowValue {
  return { text, chips: [], plain: false }
}

function chipsValue(chips: string[]): RowValue {
  return { text: '', chips, plain: false }
}

export function rowLabel(labels: SetupLabels, capabilityId: string, key: string): string {
  const labelKey = ROW_LABEL_KEYS[capabilityId]?.[key]
  return labelKey ? labels.t(`${K}.row.${labelKey}`) : humanize(key)
}

export function setupSubject(labels: SetupLabels, capabilityId: string): string {
  return labels.t(`${K}.subject.${SUBJECT_KEYS[capabilityId] ?? 'setting'}`)
}

export function confirmLabel(labels: SetupLabels, card: SetupCard): string {
  if (card.action === 'update') {
    return labels.t(`${K}.confirm.update`, {
      subject: setupSubject(labels, card.capabilityId),
    })
  }
  return labels.t(`${K}.confirm.${CONFIRM_KEYS[card.capabilityId] ?? 'default'}`)
}

export function channelLabel(labels: SetupLabels, channel: unknown): string {
  const key = CHANNEL_KEYS[String(channel ?? '').toLowerCase()]
  return key ? labels.t(`${K}.channel.${key}`) : scalar(channel)
}

function filterLabel(filter: unknown): string {
  if (!filter || typeof filter !== 'object') return scalar(filter)
  const f = filter as Record<string, unknown>
  const field = f.header ?? f.column_header ?? f.field ?? f.column ?? f.dimension
  const operator = f.match_mode_header ?? f.match_mode ?? f.operator
  const value = Array.isArray(f.value) ? f.value.join(', ') : f.value
  const parts = [field, operator, value]
    .filter((part) => !isBlank(part))
    .map((part) => scalar(part))
  return parts.length ? parts.join(' ') : scalar(filter)
}

export function metricLabel(labels: SetupLabels, metric: unknown): string {
  return typeof metric === 'string' && metric ? labels.metric(metric) : scalar(metric)
}

function thresholdLabel(labels: SetupLabels, threshold: unknown): string {
  if (!threshold || typeof threshold !== 'object') return scalar(threshold)
  const th = threshold as Record<string, unknown>
  const metric = th.metric ? metricLabel(labels, th.metric) : ''
  const operator = scalar(th.operator)
  if (typeof th.percentile === 'number') {
    const percent = Math.round(th.percentile * 1000) / 10
    const key = operator.startsWith('<') ? 'falls' : 'rises'
    return labels.t(`${K}.threshold.${key}`, { metric, percent }).trim()
  }
  return [metric, operator, th.value]
    .filter((part) => !isBlank(part))
    .map((part) => scalar(part))
    .join(' ')
}

function alertRowValue(labels: SetupLabels, row: SetupCardRow): RowValue | null {
  const { t } = labels
  const f = row.fields
  switch (row.key) {
    case 'metrics': {
      const chips = items(f.metrics ?? f.metric).map((metric) => metricLabel(labels, metric))
      return chips.length ? chipsValue(chips) : null
    }
    case 'group_by': {
      const chips = list(f.dimensions).map((dimension) => scalar(dimension))
      return chips.length ? chipsValue(chips) : plainValue(t(`${K}.value.noGrouping`))
    }
    case 'filter': {
      const chips = list(f.filters).map(filterLabel)
      return chips.length ? chipsValue(chips) : plainValue(t(`${K}.value.none`))
    }
    case 'date_range': {
      const windowKey = COMPARE_WINDOW_KEYS[scalar(f.compare_window)]
      const window = f.compare_window
        ? windowKey
          ? t(`${K}.window.${windowKey}`)
          : scalar(f.compare_window)
        : ''
      const parts = [
        window ? t(`${K}.value.versus`, { window: window.toLowerCase() }) : '',
        f.comparison_mode ? humanize(scalar(f.comparison_mode)) : '',
      ]
      const label = parts.filter(Boolean).join(' · ')
      return label ? textValue(label) : null
    }
    case 'trigger_alert_when': {
      const chips = list(f.thresholds).map((threshold) => thresholdLabel(labels, threshold))
      // Before a draft, the first message's reading holds the rule as a direction and a number.
      if (!chips.length && !isBlank(f.threshold)) {
        chips.push(
          [f.direction, f.threshold]
            .filter(Boolean)
            .map((part) => scalar(part))
            .join(' '),
        )
      }
      if (typeof f.deviation_percentage === 'number') {
        chips.push(t(`${K}.threshold.deviation`, { percent: f.deviation_percentage }))
      }
      if (!chips.length && !isBlank(f.threshold_type)) {
        const typeKey = THRESHOLD_TYPE_KEYS[scalar(f.threshold_type)]
        chips.push(typeKey ? t(`${K}.threshold.${typeKey}`) : humanize(scalar(f.threshold_type)))
      }
      if (chips.length > 1 && f.condition_operator) {
        chips.push(t(`${K}.threshold.${f.condition_operator === 'AND' ? 'all' : 'any'}`))
      }
      return chipsValue(chips)
    }
    case 'notification': {
      const emails = items(f.allowed_emails).map((email) => scalar(email))
      const email = t(`${K}.channel.email`)
      const chips = items(f.channel_type).map((channel) => {
        const label = channelLabel(labels, channel)
        return label === email && emails.length ? `${email} → ${emails.join(', ')}` : label
      })
      return chipsValue(chips)
    }
    case 'frequency':
    case 'state':
      return textValue(humanize(scalar(Object.values(f)[0])))
    default:
      return null
  }
}

// A KPI file's mapping crosses the wire as lists of pairs (`interaction_gateway.md`).
function pairs(labels: SetupLabels, value: unknown, from: string, to: string): string[] {
  return list(value)
    .filter((pair): pair is Record<string, unknown> => Boolean(pair) && typeof pair === 'object')
    .map(
      (pair) =>
        `${scalar(pair[from])} → ${to === 'metric' ? metricLabel(labels, pair[to]) : scalar(pair[to])}`,
    )
}

function kpiRowValue(labels: SetupLabels, row: SetupCardRow): RowValue | null {
  const { t } = labels
  const f = row.fields
  switch (row.key) {
    case 'period': {
      const range = [f.start_date, f.end_date]
        .filter((date) => !isBlank(date))
        .map((date) => scalar(date))
        .join(' – ')
      const repeats = isBlank(f.recurrence)
        ? ''
        : t(`${K}.value.repeats`, { recurrence: scalar(f.recurrence) })
      const text = [range, repeats].filter(Boolean).join(' · ')
      return text ? textValue(text) : null
    }
    case 'file': {
      // The file's ref and layout mean nothing to a reader; what maps to what does.
      const chips = [
        ...(isBlank(f.breakdown_column)
          ? []
          : [t(`${K}.file.breakdown`, { column: scalar(f.breakdown_column) })]),
        ...(isBlank(f.sub_breakdown_column)
          ? []
          : [t(`${K}.file.subBreakdown`, { column: scalar(f.sub_breakdown_column) })]),
        ...pairs(labels, f.metric_columns, 'column', 'metric'),
        ...pairs(labels, f.metric_values, 'label', 'metric'),
        ...pairs(labels, f.breakdown_value_choices, 'file_value', 'value'),
        ...list(f.new_breakdown_values).map((value) =>
          t(`${K}.file.newValue`, { value: scalar(value) }),
        ),
      ]
      return chips.length ? chipsValue(chips) : null
    }
    default:
      return null
  }
}

/** One row's value, formatted. Rows with no special shape list their inputs as they are. */
export function rowValue(labels: SetupLabels, capabilityId: string, row: SetupCardRow): RowValue {
  const special =
    capabilityId === ALERT_WRITE
      ? alertRowValue(labels, row)
      : capabilityId === KPI_WRITE
        ? kpiRowValue(labels, row)
        : null
  if (special) return special
  const values = Object.values(row.fields).filter((value) => !isBlank(value))
  if (!values.length) return plainValue(labels.t(`${K}.value.none`))
  if (values.length === 1 && !Array.isArray(values[0])) {
    return textValue(scalar(values[0], labels.t))
  }
  return chipsValue(
    values.flatMap((value) => list(value).map((item) => scalar(item, labels.t))).filter(Boolean),
  )
}
